import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageToast from "sap/m/MessageToast";
import Dialog from "sap/m/Dialog";
import Button from "sap/m/Button";
import VBox from "sap/m/VBox";
import Text from "sap/m/Text";
import Input from "sap/m/Input";
import Label from "sap/m/Label";
import AuthService from "../service/AuthService";
import AuditLogService from "../service/AuditLogService";
import type { AuditLogEntry, AuditLogQuery } from "../service/AuditLogService";
import formatter from "../model/formatter";

const PAGE_SIZE = 50;
const TIMEZONE_STORAGE_KEY = "portal.audit.timezone";
const DEFAULT_TIMEZONE = "local";

interface ViewPrefsModel {
	timezone: string;
}

const readPersistedTimezone = (): string => {
	try {
		const stored = window.localStorage.getItem(TIMEZONE_STORAGE_KEY);
		return stored && stored.length > 0 ? stored : DEFAULT_TIMEZONE;
	} catch {
		return DEFAULT_TIMEZONE;
	}
};

const persistTimezone = (timezone: string): void => {
	try {
		window.localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
	} catch {
		/* storage unavailable (e.g. private mode) — ignore */
	}
};

interface FiltersModel {
	action: string;
	entityType: string;
	actorUserId: string;
	actorEmail: string;
	fromDate: string;
	toDate: string;
}

interface RowsModel {
	rows: AuditLogEntry[];
	offset: number;
	hasMore: boolean;
	busy: boolean;
}

/**
 * @namespace com.ang.portal.controller
 */
export default class AuditLogs extends BaseController {
	private detailsDialog: Dialog | null = null;
	public readonly formatter = formatter;

	public onInit(): void {
		this.setModel(
			new JSONModel({
				action: "",
				entityType: "",
				actorUserId: "",
				actorEmail: "",
				fromDate: "",
				toDate: "",
			} as FiltersModel),
			"auditLogsFilters",
		);
		this.setModel(
			new JSONModel({
				rows: [],
				offset: 0,
				hasMore: false,
				busy: false,
			} as RowsModel),
			"auditLogs",
		);
		this.setModel(
			new JSONModel({ timezone: readPersistedTimezone() } as ViewPrefsModel),
			"auditViewPrefs",
		);
	}

	public onTimezoneChange(): void {
		const prefs = this.getModel("auditViewPrefs") as JSONModel;
		const timezone =
			(prefs.getProperty("/timezone") as string) || DEFAULT_TIMEZONE;
		persistTimezone(timezone);
		// Force re-evaluation of every row formatter binding that references this model.
		prefs.refresh(true);
	}

	public refreshPage(): void {
		if (!AuthService.isAuthenticated()) {
			this.navTo("login", {}, true);
			return;
		}

		const userInfo = AuthService.getUserInfo();
		if (!userInfo?.roles.includes("system:admin")) {
			this.navTo("home", {}, true);
			return;
		}

		this.resetAndLoad();
	}

	public onApplyFilters(): void {
		this.resetAndLoad();
	}

	public onResetFilters(): void {
		const filters = this.getModel("auditLogsFilters") as JSONModel;
		filters.setData({
			action: "",
			entityType: "",
			actorUserId: "",
			actorEmail: "",
			fromDate: "",
			toDate: "",
		});
		this.resetAndLoad();
	}

	public onRefresh(): void {
		this.resetAndLoad();
	}

	public onLoadMore(): void {
		void this.loadPage(false);
	}

	public onShowDetails(oEvent: {
		getSource(): {
			getBindingContext(model?: string): { getObject(): AuditLogEntry } | null;
		};
	}): void {
		const ctx = oEvent.getSource().getBindingContext("auditLogs");
		const entry = ctx?.getObject();
		if (!entry) return;
		void this.openDetailsDialog(entry);
	}

	private resetAndLoad(): void {
		const rows = this.getModel("auditLogs") as JSONModel;
		rows.setProperty("/rows", []);
		rows.setProperty("/offset", 0);
		rows.setProperty("/hasMore", false);
		void this.loadPage(true);
	}

	private async loadPage(replace: boolean): Promise<void> {
		const rowsModel = this.getModel("auditLogs") as JSONModel;
		rowsModel.setProperty("/busy", true);

		try {
			const filters = (
				this.getModel("auditLogsFilters") as JSONModel
			).getData() as FiltersModel;
			const offset = (rowsModel.getProperty("/offset") as number) || 0;

			const query: AuditLogQuery = { limit: PAGE_SIZE, offset };
			if (filters.action.trim()) query.action = filters.action.trim();
			if (filters.entityType.trim())
				query.entityType = filters.entityType.trim();
			const actorIdNum = Number(filters.actorUserId);
			if (Number.isInteger(actorIdNum) && actorIdNum > 0) {
				query.actorUserId = actorIdNum;
			}
			if (filters.actorEmail.trim())
				query.actorEmail = filters.actorEmail.trim();
			if (filters.fromDate) query.fromDate = filters.fromDate;
			if (filters.toDate) query.toDate = filters.toDate;

			const fetched = await AuditLogService.find(query);
			const existing =
				(rowsModel.getProperty("/rows") as AuditLogEntry[]) || [];
			const next = replace ? fetched : existing.concat(fetched);

			rowsModel.setProperty("/rows", next);
			rowsModel.setProperty("/offset", offset + fetched.length);
			rowsModel.setProperty("/hasMore", fetched.length >= PAGE_SIZE);
		} catch (error) {
			MessageToast.show(
				error instanceof Error ? error.message : "Failed to load audit logs",
			);
		} finally {
			rowsModel.setProperty("/busy", false);
		}
	}

	private async openDetailsDialog(entry: AuditLogEntry): Promise<void> {
		const bundle = await this.getResourceBundle();

		const formatJson = (value: unknown): string =>
			value === null || value === undefined
				? "—"
				: JSON.stringify(value, null, 2);

		const content = new VBox({
			items: [
				new Text({
					text: `${bundle.getText("auditDetailAction") ?? "Action"}: ${entry.action}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailEntity") ?? "Entity"}: ${entry.entityType}${entry.entityId ? " / " + entry.entityId : ""}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailActor") ?? "Actor"}: ${entry.actorEmail ?? "(system)"} (id=${entry.actorUserId ?? "—"})`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailIp") ?? "IP"}: ${entry.ipAddress ?? "—"}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailUserAgent") ?? "UA"}: ${entry.userAgent ?? "—"}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailWhen") ?? "When"}: ${formatter.formatAuditTimestamp(
						entry.createdAt,
						(
							this.getModel("auditViewPrefs") as JSONModel | undefined
						)?.getProperty("/timezone") as string | undefined,
					)}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailBefore") ?? "Before"}:\n${formatJson(entry.beforeData)}`,
				}),
				new Text({
					text: `${bundle.getText("auditDetailAfter") ?? "After"}:\n${formatJson(entry.afterData)}`,
				}),
			],
		}).addStyleClass("sapUiSmallMargin");

		if (!this.detailsDialog) {
			this.detailsDialog = new Dialog({
				title: bundle.getText("auditDetailsTitle") ?? "Audit entry",
				contentWidth: "40rem",
				resizable: true,
				draggable: true,
				endButton: new Button({
					text: bundle.getText("dialogClose") ?? "Close",
					press: () => this.detailsDialog?.close(),
				}),
			});
			this.getView()?.addDependent(this.detailsDialog);
		}

		this.detailsDialog.destroyContent();
		this.detailsDialog.addContent(content);
		this.detailsDialog.open();
	}

	public onExportExcel(): void {
		void this.exportToExcel();
	}

	private async exportToExcel(): Promise<void> {
		const rowsModel = this.getModel("auditLogs") as JSONModel;
		rowsModel.setProperty("/busy", true);

		try {
			const filters = (
				this.getModel("auditLogsFilters") as JSONModel
			).getData() as FiltersModel;

			// Build query with current filters but fetch up to 10000 rows
			const query: AuditLogQuery = { limit: 10000, offset: 0 };
			if (filters.action.trim()) query.action = filters.action.trim();
			if (filters.entityType.trim())
				query.entityType = filters.entityType.trim();
			const actorIdNum = Number(filters.actorUserId);
			if (Number.isInteger(actorIdNum) && actorIdNum > 0) {
				query.actorUserId = actorIdNum;
			}
			if (filters.actorEmail.trim())
				query.actorEmail = filters.actorEmail.trim();
			if (filters.fromDate) query.fromDate = filters.fromDate;
			if (filters.toDate) query.toDate = filters.toDate;

			const entries = await AuditLogService.find(query);

			if (entries.length === 0) {
				await this.showToast("auditLogsExportNoData");
				return;
			}

			const timezone =
				((
					this.getModel("auditViewPrefs") as JSONModel | undefined
				)?.getProperty("/timezone") as string) || DEFAULT_TIMEZONE;

			const csvContent = this.buildCsv(entries, timezone);
			const now = new Date();
			const dateStr = `${now.getMonth() + 1}.${now.getDate()}.${String(now.getFullYear()).slice(-2)}`;
			const defaultFilename = `audit-logs.${dateStr}`;

			this.showExportDialog(csvContent, defaultFilename, entries.length);
		} catch (error) {
			MessageToast.show(
				error instanceof Error ? error.message : "Export failed",
			);
		} finally {
			rowsModel.setProperty("/busy", false);
		}
	}

	private showExportDialog(
		csvContent: string,
		defaultFilename: string,
		entryCount: number,
	): void {
		void this.openExportDialog(csvContent, defaultFilename, entryCount);
	}

	private async openExportDialog(
		csvContent: string,
		defaultFilename: string,
		entryCount: number,
	): Promise<void> {
		const bundle = await this.getResourceBundle();

		const filenameInput = new Input({
			value: defaultFilename,
			width: "100%",
			placeholder:
				bundle.getText("auditExportFilenamePlaceholder") ?? "Enter filename",
		});

		const dialog = new Dialog({
			title: bundle.getText("auditExportDialogTitle") ?? "Export Audit Logs",
			contentWidth: "20rem",
			content: [
				new VBox({
					items: [
						new Label({
							text: bundle.getText("auditExportFilenameLabel") ?? "Filename:",
							labelFor: filenameInput,
						}),
						filenameInput,
						new Text({
							text:
								bundle.getText("auditExportFilenameHint") ??
								".csv will be appended automatically",
						}).addStyleClass("sapUiTinyMarginTop"),
					],
				}).addStyleClass("sapUiSmallMargin"),
			],
			beginButton: new Button({
				text: bundle.getText("dialogOk") ?? "OK",
				type: "Emphasized",
				press: () => {
					let filename = filenameInput.getValue().trim();
					if (!filename) {
						filename = defaultFilename;
					}
					// Remove .csv if user added it, we'll add it
					if (filename.toLowerCase().endsWith(".csv")) {
						filename = filename.slice(0, -4);
					}
					this.downloadFile(
						csvContent,
						`${filename}.csv`,
						"text/csv;charset=utf-8;",
					);
					MessageToast.show(`Exported ${entryCount} entries`);
					dialog.close();
				},
			}),
			endButton: new Button({
				text: bundle.getText("dialogCancel") ?? "Cancel",
				press: () => {
					dialog.close();
				},
			}),
			afterClose: () => {
				dialog.destroy();
			},
		});

		this.getView()?.addDependent(dialog);
		dialog.open();
	}

	private buildCsv(entries: AuditLogEntry[], timezone: string): string {
		const escapeCell = (value: unknown): string => {
			if (value === null || value === undefined) return "";
			const str =
				typeof value === "object" ? JSON.stringify(value) : String(value);
			// Escape double quotes and wrap in quotes if contains comma, newline or quote
			if (/[,"\n\r]/.test(str)) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		};

		const headers = [
			"ID",
			"When",
			"Actor Email",
			"Actor ID",
			"Action",
			"Entity Type",
			"Entity ID",
			"IP Address",
			"User Agent",
			"Before Data",
			"After Data",
		];

		const rows = entries.map((e) => [
			escapeCell(e.id),
			escapeCell(formatter.formatAuditTimestamp(e.createdAt, timezone)),
			escapeCell(e.actorEmail),
			escapeCell(e.actorUserId),
			escapeCell(e.action),
			escapeCell(e.entityType),
			escapeCell(e.entityId),
			escapeCell(e.ipAddress),
			escapeCell(e.userAgent),
			escapeCell(e.beforeData),
			escapeCell(e.afterData),
		]);

		return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
	}

	private downloadFile(
		content: string,
		filename: string,
		mimeType: string,
	): void {
		const blob = new Blob([content], { type: mimeType });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}
}
